import React, { useEffect, useState } from 'react';
import { CheckOutlined, CloseOutlined, CrownOutlined } from '@ant-design/icons';
import { message } from 'antd';
import { useTranslation } from '../../../i18n';
import { getCatalogPlans } from '../../../store/request';
import type { PlanOffer } from '../../../types/catalog';
import { PlanPurchaseModal } from './PlanPurchaseModal';
import {
  cycleUnit,
  dailyAmountText,
  featureValue,
  moneyParts,
  originalMoneyText,
  isBasicPlan,
  planMemberIcon,
  pickCyclePrice,
  planKeyOf,
  type BillingCycle
} from './pricingUtils';

const PLAN_PAGE_SIZE = 20;

function FeatureMark({ value }: { value: string }) {
  if (value === 'check') return <CheckOutlined className='pricing-feature-check' />;
  if (value === 'cross') return <CloseOutlined className='pricing-feature-cross' />;
  return <span className='pricing-feature-quota'>{value}</span>;
}

export function Pricing() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<PlanOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [cycle, setCycle] = useState<BillingCycle>('month');
  const [purchaseOpen, setPurchaseOpen] = useState(false);
  const [purchasePlanCode, setPurchasePlanCode] = useState<string>();
  const [purchaseSkuCode, setPurchaseSkuCode] = useState<string>();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const collected: PlanOffer[] = [];
      let pageNum = 1;
      setLoading(true);
      try {
        while (pageNum <= 5) {
          const res = await getCatalogPlans({ pageNum, pageSize: PLAN_PAGE_SIZE });
          if (cancelled) return;
          if (res.code !== 200 || !Array.isArray(res.content)) {
            if (res.code !== -1) message.error(res.message || t('pricing.plans_load_failed'));
            if (!cancelled) setPlans(pageNum === 1 ? [] : collected);
            return;
          }
          collected.push(...res.content);
          if (res.content.length < PLAN_PAGE_SIZE) break;
          pageNum += 1;
        }
        if (!cancelled) setPlans(collected);
      } catch {
        if (!cancelled) {
          message.error(t('pricing.plans_load_failed'));
          setPlans([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const openPurchase = (plan: PlanOffer, skuCode?: string) => {
    setPurchasePlanCode(plan.planCode?.trim() || undefined);
    setPurchaseSkuCode(skuCode);
    setPurchaseOpen(true);
  };

  return (
    <div className='container pricing-page'>
      <header className='pricing-header'>
        <div className='pricing-switch' role='tablist' aria-label={t('pricing.title')}>
          <button
            type='button'
            role='tab'
            aria-selected={cycle === 'month'}
            className={`pricing-switch-btn${cycle === 'month' ? ' is-active' : ''}`}
            onClick={() => setCycle('month')}
          >
            {t('pricing.cycle_month')}
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={cycle === 'year'}
            className={`pricing-switch-btn${cycle === 'year' ? ' is-active' : ''}`}
            onClick={() => setCycle('year')}
          >
            {t('pricing.cycle_year')}
          </button>
        </div>
      </header>

      {loading ? null : plans.length === 0 ? (
        <p className='m-0 text-sm text-slate-400'>{t('pricing.no_plans')}</p>
      ) : (
        <div className={`pricing-grid${plans.length <= 2 ? ' is-compact' : ''}`}>
          {plans.map((plan, index) => {
            const planKey = planKeyOf(plan, index);
            const price = pickCyclePrice(plan, cycle);
            const parts = moneyParts(price);
            const unit = cycleUnit(price?.durationDays, t);
            const original = originalMoneyText(price);
            const daily = dailyAmountText(price);
            const basic = isBasicPlan(plan);
            const memberIcon = planMemberIcon(plan);
            const canBuy = plan.purchaseEnabled === true && Boolean(price?.skuCode?.trim()) && Boolean(parts);
            const tone = index % 2 === 0 ? 'pro' : 'deluxe';
            return (
              <article key={planKey} className={`pricing-card pricing-card--${tone}`}>
                <div className='pricing-card-head'>
                  <span
                    className={`pricing-card-icon${memberIcon ? ' is-member' : ` pricing-card-icon--${tone}`}`}
                    aria-hidden
                  >
                    {memberIcon ? <img src={memberIcon} alt='' /> : <CrownOutlined />}
                  </span>
                  <div className='pricing-card-name-row'>
                    <h2 className='pricing-card-name'>{plan.displayName?.trim() || '—'}</h2>
                  </div>
                </div>

                <div className='pricing-card-body'>
                  <div className='pricing-offer'>
                    {basic ? <p className='pricing-basic-note'>{t('pricing.basic_included')}</p> : null}
                    <div className={basic ? 'pricing-offer-stack is-reserved' : 'pricing-offer-stack'}>
                    <div className='pricing-price'>
                      {parts ? (
                        <>
                          <span className='pricing-currency'>{parts.symbol}</span>
                          <span className='pricing-amount'>{parts.amount}</span>
                          {unit ? <span className='pricing-unit'>{unit}</span> : null}
                        </>
                      ) : (
                        <span className='pricing-amount'>&nbsp;</span>
                      )}
                    </div>
                    <p className={`pricing-original${original && !basic ? '' : ' is-placeholder'}`}>
                      {original && !basic
                        ? t('pricing.original', { price: original, unit: unit ? ` ${unit}` : '' })
                        : '\u00a0'}
                    </p>
                    {(plan.prices ?? []).length === 0 || basic ? (
                      <p className={`pricing-daily${basic ? ' is-placeholder' : ''}`}>
                        {basic ? '\u00a0' : t('pricing.no_price')}
                      </p>
                    ) : daily ? (
                      <p className='pricing-daily'>{t('pricing.per_day_amount', { price: daily })}</p>
                    ) : (
                      <p className='pricing-daily is-placeholder'>&nbsp;</p>
                    )}
                    <button
                      type='button'
                      className={`pricing-buy${canBuy ? ' is-primary' : ' is-ghost'}`}
                      disabled={!canBuy || basic}
                      tabIndex={basic ? -1 : undefined}
                      onClick={() => openPurchase(plan, price?.skuCode?.trim())}
                    >
                      {canBuy ? t('pricing.buy_now') : t('pricing.unavailable')}
                    </button>
                    </div>
                  </div>

                  <ul className='pricing-features'>
                    {(plan.featureDescriptions ?? []).map((feature, featureIndex) => {
                      const value = featureValue(feature, t);
                      return (
                        <li key={`${feature.title ?? 'feature'}-${featureIndex}`} className='pricing-feature'>
                          <span>{feature.title?.trim() || '—'}</span>
                          {value ? <FeatureMark value={value} /> : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <PlanPurchaseModal
        open={purchaseOpen}
        plans={plans}
        initialPlanCode={purchasePlanCode}
        initialSkuCode={purchaseSkuCode}
        onClose={() => setPurchaseOpen(false)}
        onPaid={() => {
          setPurchaseOpen(false);
        }}
      />
    </div>
  );
}
